import React, {useEffect, useRef} from "react";
import {useImmer} from 'use-immer';
import {cn} from '@/utils';

const AccordionModal = ({
                 headline,
                 ctaLabel,
                 closeModal,
                 values,
                 getPreviewImagePath,
                 activeAccordionIndex,
                 selectTooltip,
                 parentId,
                 onItemClick,
                 loadingIndex
               }) => {
  const [componentState, updateComponentState] = useImmer({
    isTooltipExpanded: true,
    maxHeight: 544
  });

  const accordionRef = useRef();
  const selectedMaterialRef = useRef();
  const modalBody = useRef();
  const dialogRef = useRef();

  const toggleTooltip = i => {
    if (activeAccordionIndex === i) {
      updateComponentState(draft => {
        draft.isTooltipExpanded = !componentState.isTooltipExpanded
      });
    } else {
      selectTooltip(i);
      updateComponentState(draft => {
        draft.isTooltipExpanded = true
      });
    }
  }

  const handleButtonClick = ({parentId, id, is_selected}) => {
    if (values.findIndex(value => value.is_selected) === activeAccordionIndex) {
      closeModal();
    } else {
      onItemClick({parentId, id, is_selected});
    }
  }

  useEffect(() => {
    if (dialogRef.current) {
      dialogRef.current.showModal();
    }
  }, []);

  useEffect(() => {
    if (selectedMaterialRef.current) {
      selectedMaterialRef.current.focus();
    }
  }, []);

  useEffect(() => {
    if (selectedMaterialRef.current) {
      // set scroll position so that selected material is always first
      const scrollPosition = selectedMaterialRef.current.offsetTop;
      accordionRef.current.scrollTop = scrollPosition - 60;
    }
  }, [activeAccordionIndex]);

  // Modal content is split into desktop and mobile version for cleaner behaviour implementation
  return (
    <dialog
      ref={dialogRef}
      id="configurator-modal-accordion"
      aria-labelledby="configurator-modal-accordion-title"
      className="fixed inset-0 z-overlay bg-transparent p-0 w-full max-w-4xl tablet:w-11/12"
      onClick={(e) => {
        if (modalBody.current && (e.target === modalBody.current || e.target === modalBody.current.parentElement)) {
          closeModal();
        }
      }}
      onKeyDown={(e) => {
        if (e.key === 'Escape' || e.key === 'Esc') {
          closeModal();
        }
      }}
    >
      <div
        ref={modalBody}
        className="relative overflow-hidden shadow w-full h-screen tablet:max-h-[calc(100vh-5rem)]"
      >
        <div
          className="sticky z-cover top-0 h-[55px] flex items-center justify-between bg-brand-1 pl-2xl pr-sm"
        >
          <div id="configurator-modal-accordion-title" className="font-semibold text-base text-white">
            {headline}
          </div>
          <button
            onClick={() => closeModal()}
            type="button"
            className="bg-transparent border-none rounded-full h-xl w-xl"
            aria-label={Drupal.t('Close')}
          >
            <ft-icon color="white" name="close" width={24} height={24}></ft-icon>
          </button>
        </div>
        <div
          ref={accordionRef}
          className="bg-white w-full p-xl overflow-y-auto overflow-x-hidden max-h-[calc(100vh-4rem)] tablet:max-h-[calc(100vh-9rem)]"
        >
          {values.map(({id, label, sub_label}, i) => (
            <div
              key={i}
              className={cn(
                activeAccordionIndex === i ? 'border-2 border-brand-1' : 'border border-neutral-darker',
                (activeAccordionIndex === i && i > 0) ? 'border-t-3! -mt-6xs' : (i > 0 ? 'border-t-0!' : '')
              )}
            >
              <button
                ref={activeAccordionIndex === i ? selectedMaterialRef : null}
                className={cn(
                  'relative w-full flex items-center p-sm pr-4xl',
                  activeAccordionIndex === i ? 'bg-neutral-light' : 'bg-white',
                )}
                onClick={() => toggleTooltip(i)}
              >
                <If condition={getPreviewImagePath}>
                  <img
                    className="max-w-[6rem] mr-sm"
                    src={getPreviewImagePath(id)}
                    alt={label}
                  />
                </If>
                <div className="flex flex-col">
                  <div className="text-sm font-medium text-left">{label}</div>
                  <div className='text-xs text-left'>{sub_label}</div>
                </div>
                <div className='flex absolute inset-y-0 right-0 items-center pr-lg ml-2xs pointer-events-none'>
                  <ft-icon name="caret-down" color="brand"></ft-icon>
                </div>
              </button>
              <If condition={activeAccordionIndex === i && componentState.isTooltipExpanded}>
                <div className="p-sm bg-neutral-light">
                  <div
                    dangerouslySetInnerHTML={{
                      __html: values[activeAccordionIndex].tooltip,
                    }}
                  >
                  </div>
                  <div className="flex flex-col items-center mt-lg">
                    <If condition={values[activeAccordionIndex].is_out_of_stock}>
                        <span className="text-status-error-dark text-sm font-semibold">
                          {Drupal.t('Temporarily out of stock')}
                        </span>
                    </If>
                    <If condition={values[activeAccordionIndex].is_out_of_stock !== true}>
                      <ft-button
                        variant="1"
                        size="big"
                        onClick={() => handleButtonClick({
                          parentId,
                          id: values[activeAccordionIndex].id,
                          is_selected: values[activeAccordionIndex].is_selected
                        })}
                      >
                        {ctaLabel}
                      </ft-button>
                    </If>
                  </div>
                </div>
              </If>
            </div>
          ))}
        </div>
      </div>
    </dialog>
  );
};

export default AccordionModal;
